import { Component, computed, effect, inject, input } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormlyForm } from '@ngx-formly/core';
import { defer } from 'lodash-es';
import { controlValue } from '../../util/form';
import { TAG_REGEX } from '../../util/format';
import { hasPrefix, hasTag } from '../../util/tag';

@Component({
  selector: 'app-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
  host: { 'class': 'form-group' },
  imports: [ReactiveFormsModule, FormlyForm]
})
export class TagsFormComponent {
  private fb = inject(FormBuilder);

  private readonly rootControlState = controlValue(() => this.group());

  private readonly controlState0 = controlValue(() => this.tags());

  static validators = [Validators.pattern(TAG_REGEX)];

  readonly origin = input<string | undefined>('');
  readonly group = input<UntypedFormGroup | undefined>(undefined);
  readonly fieldName = input('tags');

  field = {
    type: 'tags',
    props: {
      showLabel: true,
      label: $localize`Tags: `,
      showAdd: true,
      addText: $localize`+ Add another tag`,
    },
    fieldArray: {
      focus: false,
      props: {
        label: $localize`🏷️`,
      } as any,
    },
  };

  readonly emoji = input<string | undefined>(undefined);
  readonly label = input<string | undefined>(undefined);
  readonly showLabel = input<boolean | undefined>(undefined);
  readonly add = input<string | undefined>(undefined);
  readonly showAdd = input<boolean | undefined>(undefined);

  constructor() {
    effect(() => {
      this.field.fieldArray.props.origin = this.origin();
      const emoji = this.emoji();
      if (emoji !== undefined) this.field.fieldArray.props.label = emoji;
      const label = this.label();
      if (label !== undefined) this.field.props.label = label;
      const showLabel = this.showLabel();
      if (showLabel !== undefined) this.field.props.showLabel = showLabel;
      const add = this.add();
      if (add !== undefined) this.field.props.addText = add;
      const showAdd = this.showAdd();
      if (showAdd !== undefined) this.field.props.showAdd = showAdd;
    });
  }

  readonly tags = computed(() => {
    this.rootControlState();
    return this.group()?.get(this.fieldName()) as UntypedFormArray;
  });

  readonly model = computed(() => {
    this.rootControlState();
    this.controlState0();
    return this.tags()?.value;
  });

  setTags(values: string[]) {
    if (!this.tags()) throw 'Not ready yet!';
    fillTags(this.fb, this.tags(), values);
  }

  addTag(...values: string[]) {
    if (!this.tags()) throw 'Not ready yet!';
    if (!values.length) return;
    this.field.fieldArray.focus = true;
    for (const value of values) {
      if (value) {
        this.field.fieldArray.focus = false;
        break;
      }
    }
    addTags(this.fb, this.tags(), ...values);
  }

  update() {
    defer(() => this.tags().controls.forEach(control => control.updateValueAndValidity()));
  }

  removeTag(tag: string) {
    if (!this.tags()) throw 'Not ready yet!';
    for (let i = this.tags().value.length - 1; i >= 0; i--) {
      if (hasPrefix(this.tags().value[i], tag)) {
        this.tags().removeAt(i);
      }
    }
    this.update();
  }

  removeTagAndChildren(tag: string) {
    if (!this.tags()) throw 'Not ready yet!';
    let removed = false;
    for (let i = this.tags().value.length - 1; i >= 0; i--) {
      if (hasPrefix(this.tags().value[i], tag)) {
        this.tags().removeAt(i);
        removed = true;
      }
    }
    if (removed && tag.includes('/')) {
      const parent = tag.substring(0, tag.lastIndexOf('/'));
      if (!hasTag(parent, this.tags().value)) this.addTag(parent);
    }
    if (removed) this.update();
  }
}

/**
 * Sets the tags in a tags form array in one step.
 */
export function fillTags(fb: FormBuilder | UntypedFormBuilder, tags: UntypedFormArray, values: string[]) {
  while (tags.length > values.length) tags.removeAt(tags.length - 1, { emitEvent: false });
  while (tags.length < values.length) tags.push(fb.control(''), { emitEvent: false });
  tags.setValue(values);
}

/**
 * Adds the tags missing from a tags form array.
 */
export function addTags(fb: FormBuilder | UntypedFormBuilder, tags: UntypedFormArray, ...values: string[]) {
  values = values.filter(t => t === 'placeholder' || !hasTag(t, tags.value));
  if (values.length) fillTags(fb, tags, [...tags.value, ...values]);
}
